import { useCurrentAccount } from '@application/api';
import { SeoController, useAuthModal } from '@application/components';
import { RedirectWithStatus } from '@application/components/app/App/RedirectWithStatus';
import { daysOfWeek } from '@application/constants';
import type { Route } from '@application/types';
import {
  formatApiAddress,
  formatBoolean,
  formatLanguages,
  formatProDegree,
  translatedFieldWithFallback,
  useRouter,
  useSlideshow,
  useTranslatedUrl,
} from '@application/utils';
import { Analytics } from '@application/utils/analytics';
import { CountryCode, EducatorTypeEnum, ProfessionalTypeEnum } from '@gql/graphql';
import type {
  DetailListContactData,
  DetailListProPracticalData,
  DetailListScheduleData,
  LabelData,
  SlideshowItemData,
} from '@presentation/components';
import { EducatorPage } from '@presentation/pages';
import { PageSkeleton } from '@presentation/pages/Educator/components/PageSkeleton';
import type { LocationDescriptor } from 'history';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router';
import { useIncrementViewCount } from '../ProPageController/api/useIncrementViewCount';
import { useLogContactInfoClick } from '../ProPageController/api/useLogContactInfoClick';
import { useEducatorReviewList } from './api/useEducatorReviewList';
import { useGetEducator } from './api/useGetEducator';
import { useRelatedLinks } from '../ProPageController/api/useRelatedLinks';

export const EducatorPageController: FC = () => {
  const {
    language,
    countryCode,
    translate,
    infoFromCurrentPath,
    navigateToRoute,
    routeLocationDescriptor,
    getLocaleLinksForTranslations,
    translatedRoute,
  } = useRouter();
  const { currentAccountLoggedIn } = useCurrentAccount();
  const { show: showAuthModal } = useAuthModal();

  const [showOverlay, setShowOverlay] = useState(false);

  const url = useMemo(() => {
    return infoFromCurrentPath.params[infoFromCurrentPath.params.length - 1];
  }, [infoFromCurrentPath.params]);

  const { educator, educatorLoading } = useGetEducator(url);
  const { reviewList } = useEducatorReviewList(educator?.id);
  const { incrementViewCount } = useIncrementViewCount();

  useEffect(() => {
    incrementViewCount(educator?.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [educator?.id]);

  const [showContactInfo, setShowContactInfo] = useState(false);
  const { relatedLinks } = useRelatedLinks({
    proType: ProfessionalTypeEnum.EDUCATOR,
    proId: educator?.id,
    ...(educator?.address?.lat && educator?.address?.lng
      ? { nearByPoint: { lat: educator.address.lat, lng: educator.address.lng } }
      : {}),
  });

  useTranslatedUrl(educator?.translations);

  const translation = useMemo(() => {
    return educator?.translations.find((entry) => entry.language === language);
  }, [language, educator?.translations]);

  const slideshowItems = useMemo((): SlideshowItemData[] => {
    const items: SlideshowItemData[] = (educator?.imgs ?? []).map((img) => ({
      src: img.large as string,
    }));
    const video = educator?.profileVideo;
    if (video) {
      const videoIndex = educator?.profileVideoPos;
      const clampedIndex = Math.min(Math.max(videoIndex ?? 0, 0), items.length);
      items.splice(clampedIndex, 0, {
        src: video,
        isVideo: true,
      });
    }
    return items;
  }, [educator?.imgs, educator?.profileVideo, educator?.profileVideoPos]);

  const slideshow = useSlideshow(slideshowItems);

  const backLink = useMemo((): LocationDescriptor => {
    const route: Route = 'educatorList';
    return routeLocationDescriptor(route, {
      search: infoFromCurrentPath?.state?.returnSearch ?? {},
      params: infoFromCurrentPath?.state?.returnParams ?? [],
    });
  }, [
    infoFromCurrentPath?.state?.returnParams,
    infoFromCurrentPath?.state?.returnSearch,
    routeLocationDescriptor,
  ]);

  const { logContactInfoClick } = useLogContactInfoClick();

  const showContactInfoHandler = useCallback(() => {
    if (!educator?.educatorType || !educator?.id) {
      throw new Error('Missing educator type or educator id found');
    }
    setShowContactInfo(true);
    Analytics.sendGetEducatorInfosEvent();

    logContactInfoClick({ variables: { proId: educator.id } });
  }, [logContactInfoClick, educator?.id, educator?.educatorType]);

  const contactInfo = useMemo((): DetailListContactData => {
    return {
      phoneNumber: educator?.phone ?? undefined,
      mobilePhoneNumber: educator?.mobilePhone ?? undefined,
      email: educator?.email ?? undefined,
      website: {
        label: formatBoolean(translate, !!educator?.website),
        url: educator?.website ?? undefined,
      },
      address: {
        label: formatApiAddress({ ...educator?.address }),
      },
    };
  }, [
    educator?.address,
    educator?.email,
    educator?.mobilePhone,
    educator?.phone,
    educator?.website,
    translate,
  ]);

  const practicalInfo = useMemo((): DetailListProPracticalData => {
    const items: DetailListProPracticalData['items'] = {
      acceptedAnimals: !educator?.acceptedAnimals?.length
        ? undefined
        : educator?.acceptedAnimals
            .map((entry) => translate(`enum.animalType.${entry.toUpperCase()}`))
            .join(', '),
      acceptsNewClients: formatBoolean(translate, educator?.acceptsNewClients),
      practicePlaces: !educator?.practicePlaces?.length
        ? undefined
        : educator?.practicePlaces
            .map((entry) => translate(`enum.practicePlaces.${entry}`))
            .join(', '),
      shopGroomer: formatBoolean(translate, educator?.hasShop),
      paymentMethods: !educator?.paymentMethods?.length
        ? undefined
        : educator?.paymentMethods
            .map((entry) => translate(`glossary.paymentMethodsList.${entry}`))
            .join(', '),

      formations: formatProDegree(educator?.degrees),
    };

    return { items };
  }, [
    educator?.acceptedAnimals,
    educator?.acceptsNewClients,
    educator?.degrees,
    educator?.hasShop,
    educator?.paymentMethods,
    educator?.practicePlaces,
    translate,
  ]);

  const educatorSchedule = useMemo((): DetailListScheduleData | undefined => {
    if (!educator?.proSchedule?.length) return;
    if (!(educator?.proSchedule ?? []).filter((x) => (x?.avails ?? []).length)?.length) return;

    const scheduleDays = educator?.proSchedule?.map((x) => x?.dayOfWeek);
    if (!scheduleDays.length) return;

    return {
      times: new Array(7).fill(undefined).map((_, index) => {
        const dayOfWeek = daysOfWeek[index];
        if (scheduleDays.includes(dayOfWeek)) {
          const availabilities = educator.proSchedule
            .filter((x) => x?.dayOfWeek === dayOfWeek)
            .map((x) => x?.avails)
            .flat();
          return availabilities.map((availability) => {
            return `${availability?.start} - ${availability?.end}`;
          });
        }
        return [];
      }),
    };
  }, [educator?.proSchedule]);

  const onWriteReviewClickHandler = useCallback(() => {
    if (!currentAccountLoggedIn) {
      showAuthModal();
      return;
    }

    navigateToRoute('educatorReview', {
      params: [educator?.translations.find((x) => x.language === language)?.url as string],
    });
  }, [educator?.translations, currentAccountLoggedIn, language, navigateToRoute, showAuthModal]);

  const tipawPartnerRef = useRef<HTMLDivElement>(null);

  const headerTags: LabelData[] | undefined = useMemo(() => {
    const partnerTag: LabelData = {
      content: translate('glossary.credit'),
      size: 'large',
      theme: 'yellow',
      onClick: () => {
        tipawPartnerRef?.current?.scrollIntoView({ behavior: 'smooth' });
      },
    };
    const languageTags: LabelData[] =
      educator?.spokenLanguages?.map((x) => ({
        content: formatLanguages(language, [x]),
        size: 'large',
        theme: 'light',
      })) ?? [];
    if (educator?.isTipawPartner) {
      return [...languageTags, partnerTag];
    }
    return languageTags;
  }, [language, educator?.isTipawPartner, educator?.spokenLanguages, translate]);

  const shouldRedirect = useMemo(() => {
    const country = educator?.address?.country;
    return !educatorLoading && !!country && country !== countryCode && country !== CountryCode.EU;
  }, [countryCode, educator?.address?.country, educatorLoading]);

  return (
    <>
      <SeoController
        title={translation?.seoTitle ?? undefined}
        description={translation?.seoDescription ?? undefined}
        keywords={translation?.seoKeywords ?? undefined}
        thumbnail={slideshow.items.find((item) => !item.isVideo)?.src}
        localeLinks={getLocaleLinksForTranslations(
          educator?.translations,
          educator?.address?.country,
        )}
      />
      {educatorLoading && <PageSkeleton backLink={backLink} />}
      {!educatorLoading && !educator && <Navigate replace to="/404" />}
      {shouldRedirect && <RedirectWithStatus to={translatedRoute('proLanding')} status={302} />}

      {!educatorLoading && !!educator && (
        <EducatorPage
          header={{
            title: translation?.title ? translation?.title : educator?.translations[0]?.title,
            tags: headerTags,
            backLink,
          }}
          slideshow={slideshow}
          city={educator.address?.city || undefined}
          addressCoordinates={{
            lat: educator.address?.lat as number,
            lng: educator.address?.lng as number,
          }}
          description={translatedFieldWithFallback(language, educator.translations, 'description')}
          wellnessEngagement={translatedFieldWithFallback(
            language,
            educator.translations,
            'wellnessEngagement',
          )}
          extraDescription={translatedFieldWithFallback(
            language,
            educator.translations,
            'extraDescription',
          )}
          contactInfo={contactInfo}
          showContactInfo={showContactInfo}
          onShowContactInfo={showContactInfoHandler}
          practicalInfo={practicalInfo}
          educatorSchedule={educatorSchedule}
          onOverlayClick={() => setShowOverlay(false)}
          onSideCardClick={() => setShowOverlay(true)}
          showOverlay={showOverlay}
          relatedLinks={relatedLinks}
          educatorType={educator.educatorType || EducatorTypeEnum.EDUCATOR}
          reviewList={reviewList}
          onWriteReviewClick={onWriteReviewClickHandler}
          isTipawPartner={educator.isTipawPartner}
          tipawPartnerRef={tipawPartnerRef}
        />
      )}
    </>
  );
};
